import { PlanungAdminRoles, PlanungViewerRoles } from '@bp/pim-auth-constants';
import { PersonRole } from '@bp/planung-graphql-types';
import { Grid, GridColumn, GridRow, Input, LazyLoader, SliderModal } from '@bp/ui-components';
import { Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useConfirm } from '../../../hooks/useConfirm';
import { useLoadBasicData } from '../../../hooks/useLoadBasicData';
import { usePimProfilesQuery } from '../../../types/planung-graphql-client-defs';
import { UserPermissions } from '../Forms/UserPermissions';
import { UserPermissionsFormType } from '../Forms/UserPermissionsForm';
import { PimProfileTableDataType, SelectPimProfileTable } from '../Tables/SelectPimProfileTable';
import { SelectPlanungPersonTable, SelectPlanungPersonTableDataType } from '../Tables/SelectPlanungPersonTable';
import style from './AddUserModal.module.scss';

type AddUserModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onUserAdd: (data: {
    email: string;
    organizationUuid: string;
    frontendUri: string;
    planungPersonUuid: string;
    profileUuid?: string | null;
    permissions: Array<PlanungAdminRoles | PlanungViewerRoles>;
  }) => Promise<void>;
};

export const AddUserModal = ({ isOpen, onClose, onUserAdd }: AddUserModalProps) => {
  const { pimAuthClaims } = useAuthClaims();
  const { t } = useTranslation();

  const [selectedPlanungPerson, setSelectedPlanungPerson] = useState<SelectPlanungPersonTableDataType | null>(null);
  const [selectedPimProfile, setSelectedPimProfile] = useState<PimProfileTableDataType | null>(null);
  const [permissions, setPermissions] = useState<UserPermissionsFormType>({
    isAdmin: false,
    deputate: 'no-access',
    timetable: 'no-access',
    substitution: 'no-access',
  });
  const [emailInput, setEmailInput] = useState<string>('');
  const [emailValid, setEmailValid] = useState(false);
  const [emailBlocked, setEmailBlocked] = useState(false);
  const [currentSlideIndex, setCurrentSlideIndex] = useState<number>(0);

  const { confirm, ConfirmationDialog } = useConfirm({
    defaultTitle: t('users.addUserConfirmTitle'),
  });
  const getTitle = () => {
    if (currentSlideIndex === 0) {
      return t('users.selectPerson');
    }
    if (currentSlideIndex === 1) {
      return t('users.selectLogin');
    }
    if (currentSlideIndex === 2) {
      return t('users.enterEmail');
    }
    if (currentSlideIndex === 3) {
      return t('users.setPermissions');
    }
  };

  const [{ data: pimProfilesData }] = usePimProfilesQuery({
    variables: {
      organizationUuid: pimAuthClaims.getOrganizationUuid(),
    },
  });

  const pimData =
    pimProfilesData?.pimProfiles.map((p) => ({
      uuid: p.uuid,
      firstName: p.firstName,
      lastName: p.lastName,
      userEmail: p.userEmail,
      profileEmail: p.profileEmail,
      accessGranted: p.accessGranted,
    })) ?? [];

  const pimProfileUuidsWithNoAccess = pimData
    .filter((p) => p.accessGranted)
    .map((p) => {
      return p.uuid;
    });
  const { allPersons: planungPersons } = useLoadBasicData({ pause: false });

  const data: SelectPlanungPersonTableDataType[] =
    planungPersons?.people
      .filter((p) => !pimProfileUuidsWithNoAccess.includes(p.pimProfileUuid ?? ''))
      .map((person) => ({
        uuid: person.uuid,
        firstName: person.firstName,
        lastName: person.lastName,
        role: person.role === PersonRole.Teacher ? t('persons.title', { count: 1 }) : t('persons.other'),

        email: person.email ?? '',
      })) ?? [];

  return (
    <>
      <SliderModal
        nextDisabled={(!selectedPlanungPerson && currentSlideIndex === 0) || (!emailValid && currentSlideIndex === 2)}
        slides={[
          <div className={style.slide}>
            <Suspense fallback={<LazyLoader embedded forceHeight='40vh' />}>
              <SelectPlanungPersonTable
                data={data}
                selectedPlanungPerson={selectedPlanungPerson}
                onRowSelectionChange={(person) => {
                  if (currentSlideIndex === 0) {
                    setSelectedPimProfile(null);
                    setEmailBlocked(false);
                    setEmailValid(!!person?.email);
                    setEmailInput(person?.email ?? '');
                    setSelectedPlanungPerson(person);
                  }
                }}
              />
            </Suspense>
          </div>,
          <div className={style.slide}>
            {selectedPlanungPerson && currentSlideIndex === 1 && (
              <SelectPimProfileTable
                pimProfilesData={pimData}
                selectedPlanungPerson={selectedPlanungPerson}
                selectedPimProfile={selectedPimProfile}
                onRowSelectionChange={(pimProfile) => {
                  if (pimProfile && !pimProfile.userEmail) {
                    setEmailInput(pimProfile.profileEmail ?? selectedPlanungPerson.email ?? '');
                    setEmailValid(true);
                    setEmailBlocked(false);
                  } else if (pimProfile && pimProfile.userEmail) {
                    setEmailInput(pimProfile.userEmail);
                    setEmailValid(true);
                    setEmailBlocked(true);
                  } else {
                    setEmailInput('');
                    setEmailValid(false);
                    setEmailBlocked(false);
                  }
                  setSelectedPimProfile(pimProfile);
                }}
              />
            )}
          </div>,
          <div className={style.slide}>
            <Grid>
              <GridRow>
                <GridColumn width={12}>
                  <Input
                    label={t('users.emailInputLabel')}
                    placeholder={t('common.email')}
                    value={emailInput}
                    readonly={emailBlocked}
                    required={true}
                    error={!emailValid ? t('users.emailInvalid') : undefined}
                    onChange={(e) => {
                      // check if email is valid
                      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                      if (e.target.value && emailRegex.test(e.target.value)) {
                        setEmailValid(true);
                      } else {
                        setEmailValid(false);
                      }
                      setEmailInput(e.target.value);
                    }}
                    name={'email'}
                  ></Input>
                </GridColumn>
              </GridRow>
            </Grid>
          </div>,
          <div className={style.slide}>
            <UserPermissions
              setFieldValue={async (key, value) => {
                setPermissions((prev) => ({
                  ...prev,
                  [key]: value,
                }));
              }}
              values={permissions}
            />
          </div>,
        ]}
        submitDisabled={!emailValid || !selectedPlanungPerson}
        isOpen={isOpen}
        modalSettings={{
          shouldCloseOnEsc: false,
        }}
        title={getTitle() ?? ''}
        onSubmit={async () => {
          if (!selectedPlanungPerson) {
            return;
          }
          const permissionsArray: Array<PlanungAdminRoles | PlanungViewerRoles> = [];
          if (permissions.isAdmin) {
            permissionsArray.push(PlanungAdminRoles.Admin);
          } else {
            if (permissions.deputate !== 'no-access') {
              permissionsArray.push(permissions.deputate);
            }
            if (permissions.substitution !== 'no-access') {
              permissionsArray.push(permissions.substitution);
            }
            if (permissions.timetable !== 'no-access') {
              permissionsArray.push(permissions.timetable);
            }
          }

          const confirmed = await confirm({
            message: t('users.addUserConfirmMessage', {
              name: `${selectedPlanungPerson.lastName}, ${selectedPlanungPerson.firstName}`,
              email: emailInput,
            }),
          });
          if (!confirmed) {
            return;
          }
          await onUserAdd({
            email: emailInput,
            organizationUuid: pimAuthClaims.getOrganizationUuid(),
            frontendUri: import.meta.env.VITE_APP_BASE_URI,
            planungPersonUuid: selectedPlanungPerson.uuid,
            profileUuid: selectedPimProfile?.uuid,
            permissions: permissionsArray,
          });
        }}
        onClose={() => {
          setCurrentSlideIndex(0);
          setSelectedPlanungPerson(null);
          setSelectedPimProfile(null);
          setEmailInput('');
          onClose();
        }}
        onChange={async (index) => {
          setCurrentSlideIndex(index);
        }}
        startIndex={currentSlideIndex}
      />
      <ConfirmationDialog />
    </>
  );
};
